/**
 * Production-Ready Logger Service
 * 
 * Centralized logging utility for the wildlife project frontend.
 * Automatically disables console output in production while maintaining
 * structured logging for development and debugging.
 * 
 * Features:
 * - Environment-aware logging (development/production)
 * - Multiple log levels (debug, info, warn, error)
 * - Performance tracking and metrics
 * - Structured log formatting
 * - Component-specific logging contexts
 * - Memory-efficient operation logging
 */

// Log levels for different types of messages
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

// Log context for categorizing logs
export interface LogContext {
  component?: string;
  operation?: string;
  userId?: string;
  sessionId?: string;
  timestamp?: number;
}

// Performance tracking interface
export interface PerformanceMetrics {
  startTime: number;
  endTime?: number;
  duration?: number;
  operation: string;
  metadata?: Record<string, any>;
}

class Logger {
  private currentLevel: LogLevel;
  private isProduction: boolean;
  private isDevelopment: boolean;
  private performanceTracking: Map<string, PerformanceMetrics> = new Map();
  private readonly MAX_PERFORMANCE_ENTRIES = 100;

  constructor() {
    // Environment detection - Vite uses import.meta.env
    this.isProduction = import.meta.env.PROD;
    this.isDevelopment = import.meta.env.DEV;
    
    // Set log level based on environment
    this.currentLevel = this.isProduction ? LogLevel.NONE : LogLevel.DEBUG;
    
    // Initialize session tracking
    this.initializeSession();
  }

  /**
   * Initialize logging session with metadata
   */
  private initializeSession(): void {
    if (this.isDevelopment) {
      console.log('%c🐾 Wildlife Logger Initialized', 'color: #2C5F2D; font-weight: bold;', {
        environment: import.meta.env.MODE,
        logLevel: LogLevel[this.currentLevel],
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Debug level logging - detailed information for development
   */
  debug(message: string, data?: any, context?: LogContext): void {
    this.log(LogLevel.DEBUG, message, data, context);
  }

  /**
   * Info level logging - general information
   */
  info(message: string, data?: any, context?: LogContext): void {
    this.log(LogLevel.INFO, message, data, context);
  }

  /**
   * Warning level logging - potentially problematic situations
   */
  warn(message: string, data?: any, context?: LogContext): void {
    this.log(LogLevel.WARN, message, data, context);
  }

  /**
   * Error level logging - error conditions
   */
  error(message: string, error?: any, context?: LogContext): void {
    this.log(LogLevel.ERROR, message, error, context);
  }

  /**
   * Component-specific logging for easier debugging
   */
  component(componentName: string) {
    return {
      debug: (message: string, data?: any) => 
        this.debug(message, data, { component: componentName }),
      info: (message: string, data?: any) => 
        this.info(message, data, { component: componentName }),
      warn: (message: string, data?: any) => 
        this.warn(message, data, { component: componentName }),
      error: (message: string, error?: any) => 
        this.error(message, error, { component: componentName })
    };
  }

  /**
   * Operation-specific logging for tracking processes
   */
  operation(operationName: string, componentName?: string) {
    const context = { 
      operation: operationName,
      ...(componentName && { component: componentName })
    };
    
    return {
      start: (message: string, data?: any) => {
        this.debug(`[START] ${message}`, data, context);
        return this.startPerformanceTracking(operationName, data);
      },
      progress: (message: string, data?: any) => 
        this.debug(`[PROGRESS] ${message}`, data, context),
      success: (message: string, data?: any) => {
        this.info(`[SUCCESS] ${message}`, data, context);
        return this.endPerformanceTracking(operationName);
      },
      error: (message: string, error?: any) => {
        this.error(`[ERROR] ${message}`, error, context);
        return this.endPerformanceTracking(operationName);
      }
    };
  }

  /**
   * Start performance tracking for an operation
   */
  startPerformanceTracking(operationId: string, metadata?: any): string {
    const trackingId = `${operationId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.performanceTracking.set(trackingId, {
      startTime: performance.now(),
      operation: operationId,
      metadata
    });

    // Clean up old entries if we exceed the limit
    if (this.performanceTracking.size > this.MAX_PERFORMANCE_ENTRIES) {
      const firstEntry = this.performanceTracking.keys().next();
      if (firstEntry.value) {
        this.performanceTracking.delete(firstEntry.value);
      }
    }

    return trackingId;
  }

  /**
   * End performance tracking and log metrics
   */
  endPerformanceTracking(trackingId: string): PerformanceMetrics | null {
    const metrics = this.performanceTracking.get(trackingId);
    if (!metrics) return null;

    metrics.endTime = performance.now();
    metrics.duration = metrics.endTime - metrics.startTime;

    // Log performance metrics in development
    if (this.isDevelopment) {
      const category = this.categorizePerformance(metrics.duration);
      const emoji = category === 'fast' ? '⚡' : category === 'moderate' ? '⏱️' : '🐌';
      
      console.log(
        `%c${emoji} Performance: ${metrics.operation}`,
        `color: ${this.getPerformanceColor(category)}; font-weight: bold;`,
        {
          duration: `${metrics.duration.toFixed(2)}ms`,
          category,
          metadata: metrics.metadata
        }
      );
    }

    this.performanceTracking.delete(trackingId);
    return metrics;
  }

  /**
   * Get performance summary for all tracked operations
   */
  getPerformanceSummary(): Record<string, { count: number; avgDuration: number }> {
    const summary: Record<string, { durations: number[]; count: number; avgDuration: number }> = {};
    
    this.performanceTracking.forEach(metrics => {
      if (metrics.duration !== undefined) {
        if (!summary[metrics.operation]) {
          summary[metrics.operation] = { durations: [], count: 0, avgDuration: 0 };
        }
        summary[metrics.operation].durations.push(metrics.duration);
        summary[metrics.operation].count++;
      }
    });

    // Calculate averages
    Object.keys(summary).forEach(operation => {
      const durations = summary[operation].durations;
      summary[operation].avgDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
      delete (summary[operation] as any).durations; // Remove raw durations from final output
    });

    return summary;
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, data?: any, context?: LogContext): void {
    // Skip logging if level is below current threshold
    if (level < this.currentLevel) return;

    // Skip all logging in production except errors
    if (this.isProduction && level < LogLevel.ERROR) return;

    const timestamp = new Date().toISOString();
    const logContext = {
      ...context,
      timestamp,
      level: LogLevel[level]
    };

    // Format the log message
    const prefix = this.formatLogPrefix(level, context);
    const formattedMessage = `${prefix} ${message}`;

    // Choose appropriate console method and styling
    switch (level) {
      case LogLevel.DEBUG:
        console.log(
          `%c${formattedMessage}`,
          'color: #6B7280; font-size: 11px;',
          data ? { data, context: logContext } : logContext
        );
        break;
      case LogLevel.INFO:
        console.log(
          `%c${formattedMessage}`,
          'color: #2C5F2D; font-weight: 500;',
          data ? { data, context: logContext } : logContext
        );
        break;
      case LogLevel.WARN:
        console.warn(
          `%c${formattedMessage}`,
          'color: #D97706; font-weight: bold;',
          data ? { data, context: logContext } : logContext
        );
        break;
      case LogLevel.ERROR:
        console.error(
          `%c${formattedMessage}`,
          'color: #DC2626; font-weight: bold;',
          data ? { error: data, context: logContext } : logContext
        );
        break;
    }
  }

  /**
   * Format log prefix with context information
   */
  private formatLogPrefix(level: LogLevel, context?: LogContext): string {
    const levelIcon = this.getLevelIcon(level);
    const component = context?.component ? `[${context.component}]` : '';
    const operation = context?.operation ? `[${context.operation}]` : '';
    
    return `${levelIcon}${component}${operation}`;
  }

  /**
   * Get emoji icon for log level
   */
  private getLevelIcon(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG: return '🔍';
      case LogLevel.INFO: return 'ℹ️';
      case LogLevel.WARN: return '⚠️';
      case LogLevel.ERROR: return '❌';
      default: return '📝';
    }
  }

  /**
   * Categorize performance duration
   */
  private categorizePerformance(duration: number): 'fast' | 'moderate' | 'slow' {
    if (duration < 100) return 'fast';
    if (duration < 500) return 'moderate';
    return 'slow';
  }

  /**
   * Get color for performance category
   */
  private getPerformanceColor(category: 'fast' | 'moderate' | 'slow'): string {
    switch (category) {
      case 'fast': return '#10B981';
      case 'moderate': return '#F59E0B';
      case 'slow': return '#EF4444';
    }
  }

  /**
   * Set the minimum log level
   */
  setLogLevel(level: LogLevel): void {
    this.currentLevel = level;
    this.info('Log level changed', { newLevel: LogLevel[level] });
  }

  /**
   * Check if logging is enabled for a specific level
   */
  isEnabled(level: LogLevel): boolean {
    return level >= this.currentLevel && !(this.isProduction && level < LogLevel.ERROR);
  }
}

// Create and export singleton instance
export const logger = new Logger();

// Export convenience methods for easier usage
export const log = {
  debug: logger.debug.bind(logger),
  info: logger.info.bind(logger),
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
  component: logger.component.bind(logger),
  operation: logger.operation.bind(logger),
  performance: {
    start: logger.startPerformanceTracking.bind(logger),
    end: logger.endPerformanceTracking.bind(logger),
    summary: logger.getPerformanceSummary.bind(logger)
  }
};

// Development-only utilities
if (import.meta.env.DEV) {
  // Make logger available globally in development for debugging
  (window as any).wildlifeLogger = logger;
} 